import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FileText, Copy, Check } from "lucide-react";
import { useFileContent } from "@/hooks/useProjectFiles";

const MARKDOWN_EXTENSIONS = [".md", ".mdx", ".markdown"];

function isMarkdown(path: string): boolean {
  const lower = path.toLowerCase();
  return MARKDOWN_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer"
    >
      {copied ? (
        <><Check size={10} strokeWidth={1.5} className="text-emerald-400" /><span className="text-emerald-400">Copied</span></>
      ) : (
        <><Copy size={10} strokeWidth={1.5} /><span>Copy</span></>
      )}
    </button>
  );
}

function CodeBlock({ language, code }: { language: string; code: string }) {
  return (
    <div className="my-2 rounded-md border border-white/[0.06] overflow-hidden">
      <div className="flex items-center justify-between h-7 px-3 bg-[#151518] border-b border-white/[0.06]">
        <span className="text-[10px] font-mono text-zinc-500">{language || "text"}</span>
        <CopyButton text={code} />
      </div>
      <pre className="p-3 overflow-x-auto bg-[#0F0F11]" style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
        <code className="text-[12px] font-mono text-zinc-200 leading-[1.6] whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

export function DocumentViewer({ path }: { path: string | null }) {
  const { content, loading, error } = useFileContent(path);

  if (!path) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-colony-text-tertiary">
        <FileText className="h-8 w-8" strokeWidth={1} />
        <span className="text-sm">Select a file to view it</span>
      </div>
    );
  }
  if (loading) {
    return <div className="p-4 text-sm text-colony-text-tertiary">Loading...</div>;
  }
  if (error || content === null) {
    return (
      <div className="p-4 text-sm text-colony-text-tertiary">
        Cannot display this file{error ? `: ${error}` : ""}.
      </div>
    );
  }
  if (isMarkdown(path)) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="text-[13px] text-zinc-200 leading-[1.7] prose prose-invert prose-sm max-w-none
          prose-p:my-1.5 prose-li:my-0.5
          prose-code:bg-white/[0.06] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[12px] prose-code:font-mono
          prose-headings:text-zinc-100 prose-headings:mt-4 prose-headings:mb-2
          prose-a:text-cyan-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-zinc-100
          prose-table:text-[12px]
          prose-th:text-left prose-th:px-2 prose-th:py-1 prose-th:border-b prose-th:border-white/10
          prose-td:px-2 prose-td:py-1 prose-td:border-b prose-td:border-white/[0.06]">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                const codeString = String(children).replace(/\n$/, "");

                if (!match) {
                  return <code className={className} {...props}>{children}</code>;
                }

                return <CodeBlock language={match[1]} code={codeString} />;
              },
              pre({ children }) {
                return <>{children}</>;
              },
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    );
  }
  return (
    <pre className="h-full overflow-auto p-4 text-xs leading-relaxed text-zinc-200 whitespace-pre-wrap">
      {content}
    </pre>
  );
}
